import { useIntl } from 'react-intl';
import { Col, Form, FormInstance, Input, Popover, Row } from 'antd';

import { useDefaultPercentageFieldError } from '@/hooks';

import { parseCurrency } from '@/utils/number';

import { FloatingFormItem, InputDecimal } from '@/components/_common';
import {
  getPartProfitMargin,
  getPartRealCost,
} from '@/core/domain/stock/StockItems/StockItems.utils';
import { CostItem } from '@/core/domain/stock/Configuration/Configuration.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useEffect } from 'react';
import { Info } from 'react-feather';

export interface CompanyPartStockPricesFormData {
  stock: number;
  avaiableStock: number;
  reservedStock: number;
  minStock: number;
  maxStock: number;
  location?: string;
  buyPrice: number;
  ipi: number;
  freightCost: number;
  taxReplacement: number;
  fixedCost: number;
  otherExpenses: number;
  maxDiscount: number;
  icmsExemption: number;
  sellPrice: number;
  realCost: number;
  profitMargin: number;
  commissionPercentage: number;
}

interface CompanyPartStockPricesFormProps {
  form: FormInstance<CompanyPartStockPricesFormData>;
  costFields?: CostItem[];
}

function CompanyPartStockPricesForm({
  form,
  costFields,
}: CompanyPartStockPricesFormProps) {
  const intl = useIntl();
  const percentageError = useDefaultPercentageFieldError();

  const fixedCost = Form.useWatch('fixedCost', form);

  useEffect(() => {
    handlePriceChange();
  }, [fixedCost]);

  const handleSellPriceChange = () => {
    const realCost = parseCurrency(form.getFieldValue('realCost') ?? 0);
    const sellPrice = parseCurrency(form.getFieldValue('sellPrice') ?? 0);

    const profitMargin = getPartProfitMargin(realCost, sellPrice) * 100;

    form.setFieldsValue({ profitMargin });
  };

  const getPartExpenses = () => {
    const buyPrice = parseCurrency(form.getFieldValue('buyPrice'));
    const icmsExemption = parseCurrency(form.getFieldValue('icmsExemption'));
    const ipi = parseCurrency(form.getFieldValue('ipi'));
    const fixedCost = parseCurrency(form.getFieldValue('fixedCost'));
    const taxReplacement = parseCurrency(form.getFieldValue('taxReplacement'));
    const freightCost = parseCurrency(form.getFieldValue('freightCost'));
    const otherExpenses = parseCurrency(form.getFieldValue('otherExpenses'));
    const operationalExpenses = parseCurrency(
      form.getFieldValue('operationalExpenses'),
    );

    return {
      buyPrice,
      icmsExemption,
      ipi,
      taxReplacement,
      fixedCost,
      freightCost,
      otherExpenses,
      operationalExpenses,
    };
  };

  const handleFormValuesChange = (values: { [x: string]: string | number }) => {
    Object.keys(values).forEach((property) => {
      switch (property) {
        case 'buyPrice':
        case 'ipi':
        case 'taxReplacement':
        case 'freightCost':
        case 'otherExpenses':
        case 'operationalExpenses':
        case 'icmsExemption':
        case 'profitMargin':
          handlePriceChange();

          const profitMargin = form.getFieldValue('profitMargin') ?? 0 / 1000;
          const realCost = parseCurrency(form.getFieldValue('realCost'));

          const sellPrice = (profitMargin * realCost) / 100 + realCost || 0;

          form.setFieldsValue({ sellPrice });
          break;
        default:
          break;
      }
    });
  };

  const handlePriceChange = () => {
    const realCost = getPartRealCost(getPartExpenses(), costFields || []);
    form.setFieldsValue({ realCost });
  };

  const hasNotAuthorizationPriceEdit = !hasPermissions([
    Authorities.REGISTER_PART_EDIT_PRODUCT_PRICES,
  ]);

  const hasNotAuthorizationPriceView = !hasPermissions([
    Authorities.REGISTER_PART_VIEW_PRICES,
  ]);

  return (
    <Form form={form} onValuesChange={handleFormValuesChange}>
      <Row gutter={[16, 10]}>
        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="stock"
            label={intl.formatMessage({ id: 'parts.stock.domain.stock' })}
          >
            <InputDecimal disabled />
          </FloatingFormItem>
        </Col>
        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="minStock"
            label={intl.formatMessage({ id: 'parts.stock.domain.minStock' })}
          >
            <InputDecimal
              disabled={
                !hasPermissions([Authorities.REGISTER_PART_EDIT_PRODUCT_STOCK])
              }
            />
          </FloatingFormItem>
        </Col>
        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="maxStock"
            label={intl.formatMessage({ id: 'parts.stock.domain.maxStock' })}
          >
            <InputDecimal
              disabled={
                !hasPermissions([Authorities.REGISTER_PART_EDIT_PRODUCT_STOCK])
              }
            />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="buyPrice"
            label={intl.formatMessage({
              id: 'parts.stock.domain.buyPrice',
            })}
          >
            <InputDecimal
              fractions={4}
              disabled={hasNotAuthorizationPriceEdit}
            />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="ipi"
            label={intl.formatMessage({ id: 'parts.stock.domain.ipi' })}
            rules={[percentageError]}
          >
            <InputDecimal disabled={hasNotAuthorizationPriceEdit} />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="taxReplacement"
            label={intl.formatMessage({
              id: 'parts.stock.domain.taxReplacement',
            })}
          >
            <InputDecimal disabled={hasNotAuthorizationPriceEdit} />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="fixedCost"
            label={intl.formatMessage({
              id: 'parts.stock.domain.fixedCost',
            })}
          >
            <InputDecimal disabled fractions={4} />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="icmsExemption"
            label={intl.formatMessage({
              id: 'parts.stock.domain.icmsExemption',
            })}
          >
            <InputDecimal disabled={hasNotAuthorizationPriceEdit} />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="freightCost"
            label={intl.formatMessage({
              id: 'parts.stock.domain.freightCost',
            })}
          >
            <InputDecimal disabled={hasNotAuthorizationPriceEdit} />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="otherExpenses"
            label={intl.formatMessage({
              id: 'parts.stock.domain.otherExpenses',
            })}
          >
            <InputDecimal disabled={hasNotAuthorizationPriceEdit} />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="realCost"
            label={intl.formatMessage({
              id: 'parts.stock.domain.realCost',
            })}
          >
            <InputDecimal disabled fractions={4} />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="profitMargin"
            label={intl.formatMessage({
              id: 'parts.stock.domain.profitMargin',
            })}
          >
            <InputDecimal disabled={hasNotAuthorizationPriceEdit} />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="maxDiscount"
            rules={[percentageError]}
            label={intl.formatMessage({
              id: 'parts.stock.domain.maxDiscount',
            })}
          >
            <InputDecimal disabled={hasNotAuthorizationPriceEdit} />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="sellPrice"
            label={intl.formatMessage({
              id: 'parts.stock.domain.sellPrice',
            })}
          >
            <InputDecimal
              onChange={handleSellPriceChange}
              disabled={hasNotAuthorizationPriceEdit}
            />
          </FloatingFormItem>
        </Col>
        <Col
          span={24}
          sm={12}
          lg={8}
          xl={6}
          hidden={hasNotAuthorizationPriceView}
        >
          <FloatingFormItem
            name="commissionPercentage"
            label={intl.formatMessage({
              id: 'parts.stock.domain.commissionPercentage',
            })}
          >
            <InputDecimal
              disabled={hasNotAuthorizationPriceEdit}
              suffix={
                <Popover
                  content={
                    <div style={{ maxWidth: 300 }}>
                      Este valor será utilizado no cálculo e exibido no
                      comissionamento da OS, mesmo que o funcionário indicado
                      possua uma comissão configurada em seu cadastro.
                    </div>
                  }
                >
                  <Info size={18} />
                </Popover>
              }
            />
          </FloatingFormItem>
        </Col>
        <Col span={24} sm={24} lg={8} xl={12}>
          <FloatingFormItem
            name="location"
            label={intl.formatMessage({ id: 'parts.stock.domain.location' })}
          >
            <Input />
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
}

export default CompanyPartStockPricesForm;
